import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";

/** The range the server falls back to when the URL names none. */
export const DEFAULT_RANGE = "last_12_months";

/**
 * Reads and writes the ?range= search param.
 *
 * Keeping the range in the URL is what makes the drill-in honest: the account
 * list and the account page read the same param, so the charts on the detail
 * page cover exactly the months the row that linked there was summarising.
 */
export function useRangeParam(): [string, (key: string) => void] {
  const search = useSearch({ strict: false }) as { range?: string };
  const navigate = useNavigate();
  const range = search.range ?? DEFAULT_RANGE;

  // Stable identity, deliberately. Every page publishes this setter into the
  // app header, and usePageHeader rebuilds the header whenever one of its deps
  // changes: a fresh closure each render meant set state -> render -> fresh
  // closure -> set state, which React kills at 50 rounds with "Maximum update
  // depth exceeded". Nothing here reads render scope -- `prev` is supplied by
  // the router -- so there is nothing to go stale.
  const setRange = useCallback(
    (key: string) => {
      navigate({
        // "." keeps the current route (and its path params) -- this hook is
        // shared by the list and the detail page, so it must not name either.
        to: ".",
        // replace: changing the range is a re-read of the same screen, not a
        // new place -- Back should leave the screen, not step through ranges.
        replace: true,
        search: (prev) => ({
          ...prev,
          range: key === DEFAULT_RANGE ? undefined : key,
        }),
      });
    },
    [navigate],
  );

  return [range, setRange];
}
